import React, { useMemo } from 'react';
import { Component } from '@/types';
import { useDataTextValue } from '@/hooks/useDataTextValue';
import { ChartDataSource } from '@/types/chartDataSource';
import { CONTROL_THEME } from './controlThemeTokens';

// ─── Types ───────────────────────────────────────────────────────────────────

interface StatusLightThemeConfig {
  glow: boolean;
  glowIntensity: number;
  border: boolean;
  borderColor: string;
  innerHighlight: boolean;
  background: string;
}

interface Threshold {
  value: number;
  status: string;
  color: string;
}

interface StatusLightProps {
  component: Component;
}

// ─── Status Color Map ────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  ok: '#22C55E',
  warning: '#F59E0B',
  critical: '#EF4444',
  offline: '#9CA3AF',
  custom: '#6B7280',
};

// ─── Theme Presets ───────────────────────────────────────────────────────────

const LIGHT_THEMES: Record<string, StatusLightThemeConfig> = {
  led: {
    glow: true,
    glowIntensity: 8,
    border: true,
    borderColor: 'rgba(0,0,0,0.2)',
    innerHighlight: true,
    background: 'transparent',
  },
  flat: {
    glow: false,
    glowIntensity: 0,
    border: false,
    borderColor: 'transparent',
    innerHighlight: false,
    background: 'transparent',
  },
  glow: {
    glow: true,
    glowIntensity: 15,
    border: false,
    borderColor: 'transparent',
    innerHighlight: true,
    background: 'rgba(0,0,0,0.05)',
  },
  minimal: {
    glow: false,
    glowIntensity: 0,
    border: true,
    borderColor: CONTROL_THEME.border,
    innerHighlight: false,
    background: 'transparent',
  },
  'traffic-light': {
    glow: true,
    glowIntensity: 5,
    border: true,
    borderColor: '#1F2937',
    innerHighlight: true,
    background: '#111827',
  },
};

// ─── Size Presets ────────────────────────────────────────────────────────────

const SIZE_MAP: Record<string, number> = {
  sm: 20,
  md: 32,
  lg: 48,
};

// ─── Blink Keyframes ─────────────────────────────────────────────────────────

const BLINK_STYLES = `
@keyframes sl-blink-slow { 0%, 100% { opacity: 1; } 50% { opacity: 0.2; } }
@keyframes sl-blink-fast { 0%, 100% { opacity: 1; } 50% { opacity: 0.15; } }
@keyframes sl-pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.6; transform: scale(0.92); } }
`;

// ─── Component ───────────────────────────────────────────────────────────────

export const StatusLight: React.FC<StatusLightProps> = ({ component }) => {
  const properties = (component.properties || {}) as Record<string, unknown>;

  // ─── Properties ──────────────────────────────────────────────────────
  const status = (properties.status as string) ?? 'ok';
  const blinkMode = (properties.blinkMode as string) ?? 'none';
  const label = (properties.label as string) ?? '';
  const showLabel = (properties.showLabel as boolean) ?? true;
  const size = (properties.size as string) ?? 'md';
  const shape = (properties.shape as string) ?? 'circle';
  const themeName = (properties.theme as string) ?? 'led';
  const thresholds = (properties.thresholds as Threshold[] | undefined);
  const colorOverride = properties.color as string | undefined;

  // Simple threshold properties (auto-build thresholds when explicit array not set)
  const warningThreshold = properties.warningThreshold as number | undefined;
  const criticalThreshold = properties.criticalThreshold as number | undefined;
  const thresholdDirection = (properties.thresholdDirection as string) ?? 'above';

  const theme = useMemo(() => {
    return LIGHT_THEMES[themeName] ?? LIGHT_THEMES['led'];
  }, [themeName]);

  // ─── Custom Status Colors ─────────────────────────────────────────────
  const statusColors = useMemo((): Record<string, string> => {
    const colors = { ...STATUS_COLORS };
    if (properties.okColor != null) colors.ok = properties.okColor as string;
    if (properties.warningColor != null) colors.warning = properties.warningColor as string;
    if (properties.criticalColor != null) colors.critical = properties.criticalColor as string;
    if (properties.offlineColor != null) colors.offline = properties.offlineColor as string;
    return colors;
  }, [properties.okColor, properties.warningColor, properties.criticalColor, properties.offlineColor]);

  // ─── Effective Thresholds ─────────────────────────────────────────────
  const effectiveThresholds = useMemo(() => {
    if (thresholds && thresholds.length > 0) return thresholds;
    if (warningThreshold == null && criticalThreshold == null) return undefined;

    const built: Threshold[] = [];
    if (thresholdDirection === 'below') {
      // Lower is worse: value <= critical → critical, value <= warning → warning
      if (criticalThreshold != null) built.push({ value: criticalThreshold, status: 'critical', color: statusColors.critical });
      if (warningThreshold != null) built.push({ value: warningThreshold, status: 'warning', color: statusColors.warning });
      built.push({ value: Infinity, status: 'ok', color: statusColors.ok });
    } else {
      // Higher is worse (default): value >= critical → critical, value >= warning → warning
      if (criticalThreshold != null) built.push({ value: criticalThreshold, status: 'critical', color: statusColors.critical });
      if (warningThreshold != null) built.push({ value: warningThreshold, status: 'warning', color: statusColors.warning });
      built.push({ value: -Infinity, status: 'ok', color: statusColors.ok });
    }
    return built;
  }, [thresholds, warningThreshold, criticalThreshold, thresholdDirection, statusColors]);

  // ─── Data Binding ────────────────────────────────────────────────────
  const dataSource = properties.dataSource as ChartDataSource | undefined;
  const templateId = properties.queryTemplateId as string | undefined;
  const valueField = (properties.valueField as string) ?? 'value';

  const { value: rawValue } = useDataTextValue({
    dataSource,
    componentId: component.id,
    valueField,
    staticValue: dataSource ? undefined : undefined,
    templateId,
  });

  // ─── Determine effective status & color ──────────────────────────────
  const { effectiveStatus, effectiveColor } = useMemo(() => {
    // If data source with thresholds, determine status from value
    if (dataSource && rawValue !== null && effectiveThresholds && effectiveThresholds.length > 0) {
      const numVal = typeof rawValue === 'number' ? rawValue : parseFloat(String(rawValue));
      if (!isNaN(numVal)) {
        if (thresholdDirection === 'below') {
          // Lower is worse: sort ascending, pick first where value <= threshold
          const sorted = [...effectiveThresholds].sort((a, b) => a.value - b.value);
          for (const t of sorted) {
            if (numVal <= t.value) {
              return { effectiveStatus: t.status, effectiveColor: t.color || statusColors[t.status] || statusColors.custom };
            }
          }
          const highest = sorted[sorted.length - 1];
          return { effectiveStatus: highest.status, effectiveColor: highest.color || statusColors[highest.status] || statusColors.custom };
        } else {
          // Higher is worse (default): sort descending, pick first where value >= threshold
          const sorted = [...effectiveThresholds].sort((a, b) => b.value - a.value);
          for (const t of sorted) {
            if (numVal >= t.value) {
              return { effectiveStatus: t.status, effectiveColor: t.color || statusColors[t.status] || statusColors.custom };
            }
          }
          const lowest = sorted[sorted.length - 1];
          return { effectiveStatus: lowest.status, effectiveColor: lowest.color || statusColors[lowest.status] || statusColors.custom };
        }
      }
    }

    const s = status;
    const c = colorOverride || statusColors[s] || statusColors.custom;
    return { effectiveStatus: s, effectiveColor: c };
  }, [dataSource, rawValue, effectiveThresholds, thresholdDirection, status, colorOverride, statusColors]);

  // ─── Dimensions ──────────────────────────────────────────────────────
  const lightSize = SIZE_MAP[size] ?? 32;

  // ─── Blink animation ────────────────────────────────────────────────
  const effectiveBlinkMode = useMemo(() => {
    const blinkOnStatus = properties.blinkOnStatus as string | undefined;
    if (blinkOnStatus && effectiveStatus === blinkOnStatus) return 'fast';
    return blinkMode;
  }, [properties.blinkOnStatus, effectiveStatus, blinkMode]);

  const animationStyle: React.CSSProperties = useMemo(() => {
    switch (effectiveBlinkMode) {
      case 'slow': return { animation: 'sl-blink-slow 2s ease-in-out infinite' };
      case 'fast': return { animation: 'sl-blink-fast 0.5s ease-in-out infinite' };
      case 'pulse': return { animation: 'sl-pulse 1.5s ease-in-out infinite' };
      default: return {};
    }
  }, [effectiveBlinkMode]);

  // ─── Shape rendering ────────────────────────────────────────────────
  const svgSize = lightSize + (theme.glow ? theme.glowIntensity * 2 : 0);
  const center = svgSize / 2;
  const radius = lightSize / 2;

  const renderShape = () => {
    const fill = effectiveColor;
    const stroke = theme.border ? theme.borderColor : 'none';
    const strokeWidth = theme.border ? 1.5 : 0;

    const glowFilter = theme.glow ? 'url(#sl-glow)' : undefined;

    switch (shape) {
      case 'square':
        return (
          <rect
            x={center - radius}
            y={center - radius}
            width={lightSize}
            height={lightSize}
            rx={3}
            fill={fill}
            stroke={stroke}
            strokeWidth={strokeWidth}
            filter={glowFilter}
          />
        );
      case 'diamond':
        return (
          <rect
            x={center - radius * 0.7}
            y={center - radius * 0.7}
            width={lightSize * 0.7}
            height={lightSize * 0.7}
            rx={2}
            fill={fill}
            stroke={stroke}
            strokeWidth={strokeWidth}
            filter={glowFilter}
            transform={`rotate(45 ${center} ${center})`}
          />
        );
      default: // circle
        return (
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill={fill}
            stroke={stroke}
            strokeWidth={strokeWidth}
            filter={glowFilter}
          />
        );
    }
  };

  return (
    <div
      data-testid="status-light"
      role="status"
      aria-label={`Status: ${effectiveStatus}${label ? ` - ${label}` : ''}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        gap: 4,
        minWidth: 0,
        minHeight: 0,
        padding: 4,
        boxSizing: 'border-box',
        userSelect: 'none',
      }}
    >
      <style>{BLINK_STYLES}</style>

      {/* Light SVG */}
      <div style={{
        ...animationStyle,
        lineHeight: 0,
        background: theme.background,
        borderRadius: shape === 'circle' ? '50%' : shape === 'diamond' ? '4px' : '4px',
        padding: theme.glow ? theme.glowIntensity : 0,
      }}>
        <svg width={svgSize} height={svgSize} viewBox={`0 0 ${svgSize} ${svgSize}`}>
          <defs>
            {theme.glow && (
              <filter id="sl-glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation={theme.glowIntensity / 2} result="blur" />
                <feFlood floodColor={effectiveColor} floodOpacity="0.6" result="color" />
                <feComposite in="color" in2="blur" operator="in" result="glow" />
                <feMerge>
                  <feMergeNode in="glow" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            )}
          </defs>
          {renderShape()}
          {/* Inner highlight for LED/glow themes */}
          {theme.innerHighlight && shape === 'circle' && (
            <circle
              cx={center - radius * 0.25}
              cy={center - radius * 0.25}
              r={radius * 0.2}
              fill="rgba(255,255,255,0.4)"
            />
          )}
        </svg>
      </div>

      {/* Label */}
      {showLabel && label && (
        <span style={{
          fontSize: Math.max(10, lightSize * 0.35),
          color: CONTROL_THEME.mutedForeground,
          textAlign: 'center',
          lineHeight: 1.2,
          maxWidth: lightSize * 3,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {label}
        </span>
      )}
    </div>
  );
};
